// ── SETTINGS PAGE SECTIONS (fp-nofodesk-9) — extracted from App.js ──
import { useState, useEffect, createContext, useContext } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, useT } from "../theme";
import { useWindowWidth } from "../hooks/useWindowWidth";
import { useWorkspace } from "../context/WorkspaceContext";

import { API, apiFetch } from "../utils/api";
import { loadOrgProfile, ORG_KEY } from "../utils/grants";

// ── ChangePasswordSection ─────────────────────────────────────
function ChangePasswordSection({ authUser }) {
  const T = useT();
  const [form, setForm]     = useState({ current: "", next: "", confirm: "" });
  const [msg, setMsg]       = useState(null); // { type: "ok"|"err", text }
  const [loading, setLoading] = useState(false);

  if (!authUser || authUser.provider !== "email") return null;

  const inputS = { width: "100%", padding: "8px 12px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.textBody, fontSize: 13, fontFamily: FONT_H, outline: "none", boxSizing: "border-box" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!form.current || !form.next || !form.confirm) { setMsg({ type: "err", text: "All fields are required." }); return; }
    if (form.next.length < 8) { setMsg({ type: "err", text: "New password must be at least 8 characters." }); return; }
    if (form.next !== form.confirm) { setMsg({ type: "err", text: "New passwords do not match." }); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/auth/change-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword: form.current, newPassword: form.next }),
      });
      const data = await res.json();
      if (!res.ok) { setMsg({ type: "err", text: data.error || "Failed to change password." }); return; }
      setMsg({ type: "ok", text: "Password changed successfully." });
      setForm({ current: "", next: "", confirm: "" });
    } catch { setMsg({ type: "err", text: "Network error. Please try again." }); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ padding: "24px 28px", borderTop: `1px solid ${T.border}`, marginTop: 8 }}>
      <h3 style={{ margin: "0 0 18px", fontSize: 17, fontFamily: FONT_H, fontWeight: 400, color: T.text }}>🔒 Change Password</h3>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 400 }}>
        <div>
          <label style={{ fontSize: 10, color: T.textFaint, fontFamily: FONT_H, letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>Current Password</label>
          <input type="password" value={form.current} onChange={e => setForm(f => ({...f, current: e.target.value}))} placeholder="Enter current password" style={inputS} autoComplete="current-password" />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textFaint, fontFamily: FONT_H, letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>New Password</label>
          <input type="password" value={form.next} onChange={e => setForm(f => ({...f, next: e.target.value}))} placeholder="Min. 8 characters" style={inputS} autoComplete="new-password" />
        </div>
        <div>
          <label style={{ fontSize: 10, color: T.textFaint, fontFamily: FONT_H, letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>Confirm New Password</label>
          <input type="password" value={form.confirm} onChange={e => setForm(f => ({...f, confirm: e.target.value}))} placeholder="Repeat new password" style={inputS} autoComplete="new-password" />
        </div>
        {msg && (
          <div style={{ padding: "8px 12px", borderRadius: 6, fontSize: 13, fontFamily: FONT_H,
            background: msg.type === "ok" ? "#052e16" : "#1a0808",
            border: `1px solid ${msg.type === "ok" ? "#166534" : "#7f1d1d"}`,
            color: msg.type === "ok" ? "#4ade80" : "#fca5a5" }}>
            {msg.text}
          </div>
        )}
        <button type="submit" disabled={loading} style={{ padding: "9px 20px", background: T.gold, color: "#0A1628", border: "none", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, fontSize: 13, fontFamily: FONT_H, fontWeight: 600, alignSelf: "flex-start" }}>
          {loading ? "Saving…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}

// ── OrgProfileView ────────────────────────────────────────────
const ProfileCtx = createContext({});

function ProfileField({ label, field, placeholder, multi }) {
  const T = useT();
  const { profile, handleChange } = useContext(ProfileCtx);
  const inputStyle = { background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.textBody, padding: "8px 12px", fontSize: 13, fontFamily: FONT_H, outline: "none", width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11, color: T.textFaint, letterSpacing: "0.05em", fontFamily: FONT_H }}>{label}</label>
      {multi
        ? <textarea value={profile[field]} onChange={(e) => handleChange(field, e.target.value)} placeholder={placeholder} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
        : <input type="text" value={profile[field]} onChange={(e) => handleChange(field, e.target.value)} placeholder={placeholder} style={inputStyle} />}
    </div>
  );
}

// ── DataPrivacySection ────────────────────────────────────────
function DataPrivacySection({ authUser }) {
  const T = useT();
  const [events, setEvents] = useState(null);
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(false);

  const loadLog = async () => {
    if (events) { setOpen(o => !o); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/user/audit-log`);
      const d   = await res.json();
      setEvents(d.events || []);
      setOpen(true);
    } catch { setEvents([]); setOpen(true); }
    finally { setLoading(false); }
  };

  // Everything held for the account, as one JSON file (GET /api/user/export).
  const [exporting, setExporting] = useState(false);
  const downloadData = async () => {
    setExporting(true);
    try {
      const res = await apiFetch(`${API}/user/export`);
      if (!res.ok) throw new Error("export failed");
      const blob = new Blob([JSON.stringify(await res.json(), null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `nofodesk-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch { alert("Your data could not be downloaded. Please try again."); }
    setExporting(false);
  };

  const card = { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px", marginBottom: 16 };
  const heading = { fontSize: 14, fontWeight: 700, color: T.textPrimary, marginBottom: 12 };
  const row = { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 };
  const dot = (color) => ({ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, marginTop: 4 });
  const label = { fontSize: 13, color: T.textSecondary, lineHeight: 1.5 };

  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={card}>
        <div style={heading}>Data Privacy & Security</div>
        <div style={{ marginBottom: 16, fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
          NofoDesk processes your grant documents to extract structured information. Here is exactly what we do and do not do with your data.
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
          <button onClick={downloadData} disabled={exporting}
            style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: "transparent", color: T.textPrimary, fontSize: 13, fontWeight: 600, cursor: exporting ? "wait" : "pointer" }}>
            {exporting ? "Preparing…" : "Download my data"}
          </button>
          <span style={{ fontSize: 12.5, color: T.textMuted }}>
            <a href="/legal/subprocessors.html" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>Sub-processors</a>
            {" · "}<a href="/legal/data-retention.html" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>Data retention</a>
            {" · "}<a href="/legal/ai-use-policy.html" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>AI use policy</a>
          </span>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: TOKENS.statusGreen, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>What we do</div>
          {[
            "Grant documents are processed in memory only — never written to disk or stored in our database.",
            "Your Google Drive and OneDrive access credentials are encrypted before storage.",
            "Every request is authenticated — only you can access your account and data.",
            "Your account is protected against unauthorised access attempts and abuse.",
            "An audit log records key account events (login, extraction, drive connection) for your review below.",
          ].map((item, i) => (
            <div key={i} style={row}>
              <div style={dot(TOKENS.statusGreen)} />
              <span style={label}>{item}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: TOKENS.statusAmber, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>What we don't do</div>
          {[
            "We do not sell or share your documents or grant data with third parties.",
            "We do not train AI models on your content — documents are sent to our AI provider for extraction only.",
            "We do not store raw file bytes or document text after extraction is complete.",
            "We do not log the content of AI conversations beyond what is needed for your session.",
          ].map((item, i) => (
            <div key={i} style={row}>
              <div style={dot(TOKENS.statusAmber)} />
              <span style={label}>{item}</span>
            </div>
          ))}
        </div>

        <button
          onClick={loadLog}
          disabled={loading}
          style={{ fontSize: 13, padding: "8px 16px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.bgElevated, color: T.textSecondary, cursor: "pointer", marginTop: 4 }}
        >
          {loading ? "Loading…" : open ? "Hide activity log" : "View my activity log"}
        </button>

        {open && events !== null && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
            {events.length === 0 ? (
              <div style={{ fontSize: 13, color: T.textMuted }}>No events recorded yet.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: T.textMuted, textAlign: "left" }}>
                    <th style={{ padding: "4px 8px", fontWeight: 600 }}>When</th>
                    <th style={{ padding: "4px 8px", fontWeight: 600 }}>Action</th>
                    <th style={{ padding: "4px 8px", fontWeight: 600 }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(ev => (
                    <tr key={ev.id} style={{ borderTop: `1px solid ${T.border}` }}>
                      <td style={{ padding: "6px 8px", color: T.textMuted, whiteSpace: "nowrap" }}>{new Date(ev.created_at).toLocaleString()}</td>
                      <td style={{ padding: "6px 8px", color: T.textSecondary }}>{ev.action}</td>
                      <td style={{ padding: "6px 8px", color: T.textFaint }}>{ev.resource || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── DeleteAccountSection ──────────────────────────────────────
function DeleteAccountSection({ onDeleted }) {
  const T = useT();
  const [open,    setOpen]    = useState(false);
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleDelete = async () => {
    if (confirm !== 'DELETE') return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`${API}/user/account`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to delete account'); }
      onDeleted();
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '0 24px 28px' }}>
      <div style={{ background: T.bgCard || T.bgElevated, border: '1px solid #ef4444', borderRadius: 10, padding: '16px 20px' }}>
        <div style={{ fontFamily: FONT_UI, fontSize: 13, fontWeight: 700, color: '#ef4444', marginBottom: 4 }}>Danger Zone</div>
        <div style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
          Permanently delete your account and all associated data including grants, applications, files, and settings. This cannot be undone.
        </div>
        <button
          onClick={() => { setOpen(true); setConfirm(''); setError(''); }}
          style={{ padding: '7px 16px', borderRadius: 6, background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_UI }}
        >
          Delete My Account
        </button>
      </div>

      {open && (
        <>
          <div onClick={() => !loading && (setOpen(false), setConfirm(''), setError(''))} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 900 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, maxWidth: '90vw', background: T.bgSurface, border: '1px solid #ef4444', borderRadius: 12, zIndex: 901, padding: '28px 28px 24px' }}>
            <div style={{ fontFamily: FONT_UI, fontSize: 15, fontWeight: 700, color: '#ef4444', marginBottom: 10 }}>Delete your account?</div>
            <div style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, marginBottom: 20, lineHeight: 1.6 }}>
              This will permanently delete all your grants, applications, notes, files, and account data. <strong style={{ color: T.text }}>This cannot be undone.</strong>
            </div>
            <div style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textSub, marginBottom: 6 }}>
              Type <strong style={{ color: T.text }}>DELETE</strong> to confirm:
            </div>
            <input
              value={confirm}
              onChange={e => setConfirm(e.target.value.toUpperCase())}
              placeholder="DELETE"
              autoFocus
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${confirm === 'DELETE' ? '#ef4444' : T.border}`, background: T.bgElevated, color: T.text, fontSize: 13, fontFamily: FONT_UI, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }}
            />
            {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setOpen(false); setConfirm(''); setError(''); }}
                disabled={loading}
                style={{ padding: '8px 18px', borderRadius: 6, background: 'transparent', border: `1px solid ${T.border}`, color: T.textSub, fontSize: 13, fontFamily: FONT_UI, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={confirm !== 'DELETE' || loading}
                style={{ padding: '8px 18px', borderRadius: 6, background: confirm === 'DELETE' ? '#ef4444' : T.border, border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: confirm === 'DELETE' && !loading ? 'pointer' : 'not-allowed', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Deleting…' : 'Delete Account'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const EMPTY_PROFILE = { name: "", type: "", country: "", focusAreas: "", registrationNumber: "", yearFounded: "", annualBudget: "", staffCount: "", existingDonors: "", certifications: "", notes: "" };

function OrgProfileView({ onProfileChange }) {
  const T = useT();
  const isMobile = useWindowWidth() < 768;
  const [profile, setProfile] = useState(() => loadOrgProfile() || { ...EMPTY_PROFILE });
  const [saved,   setSaved]   = useState(!!loadOrgProfile());
  const [dirty,   setDirty]   = useState(false);

  const handleChange = (field, val) => { setProfile((p) => ({ ...p, [field]: val })); setDirty(true); setSaved(false); };
  const handleSave   = () => { setSaved(true); setDirty(false); onProfileChange(profile); };
  const handleClear  = () => { setProfile({ ...EMPTY_PROFILE }); localStorage.removeItem(ORG_KEY); setSaved(false); setDirty(false); onProfileChange(null); };

  return (
    <ProfileCtx.Provider value={{ profile, handleChange }}>
    <div style={{ padding: isMobile ? "16px 14px" : "24px", maxWidth: 720, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ color: T.text, margin: 0, fontSize: 20, fontFamily: FONT_H }}>🏢 Organisation Profile</h2>
          <div style={{ fontSize: 12, color: T.textFaint, marginTop: 4, fontFamily: FONT_H }}>Saved locally. Used to assess eligibility when you extract a grant.</div>
        </div>
        {saved && !dirty && <span style={{ fontSize: 11, color: "#4ade80", fontFamily: "inherit" }}>✓ Saved</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
        <ProfileField label="Organisation Name"      field="name"               placeholder="e.g. Health Bridge Africa" />
        <ProfileField label="Organisation Type"      field="type"               placeholder="e.g. NGO, University, CBO, Government" />
        <ProfileField label="Country / Region"       field="country"            placeholder="e.g. Kenya, Sub-Saharan Africa" />
        <ProfileField label="Year Founded"           field="yearFounded"        placeholder="e.g. 2010" />
        <ProfileField label="Registration Number"    field="registrationNumber" placeholder="Official registration ID" />
        <ProfileField label="Annual Budget (USD)"    field="annualBudget"       placeholder="e.g. $500,000" />
        <ProfileField label="Staff Count"            field="staffCount"         placeholder="e.g. 25 full-time" />
        <ProfileField label="Certifications"         field="certifications"     placeholder="e.g. ISO 9001, SAM registered" />
        <div style={{ gridColumn: "1 / -1" }}>
          <ProfileField label="Focus Areas / Thematic Areas" field="focusAreas" placeholder="e.g. HIV/AIDS, maternal health, WASH" multi />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <ProfileField label="Existing Donors / Prior Grants" field="existingDonors" placeholder="e.g. USAID, Gates Foundation (prior grantee), WHO" multi />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <ProfileField label="Additional Notes" field="notes" placeholder="Any other details relevant to eligibility checks..." multi />
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 20, alignItems: "center" }}>
        <button onClick={handleSave}  style={{ padding: "9px 22px", background: TOKENS.gold, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontFamily: FONT_H, fontWeight: 600 }}>Save Profile</button>
        <button onClick={handleClear} style={{ padding: "9px 16px", background: "transparent", color: T.textFaint, border: `1px solid ${T.border}`, borderRadius: 6, cursor: "pointer", fontSize: 13, fontFamily: FONT_H }}>Clear</button>
        {dirty && <span style={{ fontSize: 11, color: "#f59e0b", fontFamily: FONT_H }}>Unsaved changes</span>}
      </div>

      <div style={{ marginTop: 24, padding: "14px 16px", background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8 }}>
        <div style={{ fontSize: 12, color: T.textFaint, fontFamily: FONT_H, lineHeight: 1.8 }}>
          <strong style={{ color: T.textMuted }}>How to use:</strong><br />
          1. Fill in your organisation details and click Save.<br />
          2. Go to the Extract tab and extract any grant.<br />
          3. Click "🔍 Check Eligibility" in the extraction result to get an AI assessment.
        </div>
      </div>

      {/* Feature 10: Team Sharing */}
      <TeamSharingSection T={T} />
    </div>
    </ProfileCtx.Provider>
  );
}

const VISITOR_TABLES = [
  { key: 'active-grants',     label: 'Active Grants'       },
  { key: 'pipeline-apps',     label: 'Applications'        },
  { key: 'terminated-grants', label: 'Terminated Grants'   },
  { key: 'completed-grants',  label: 'Completed Grants'    },
  { key: 'tracked-grants',    label: 'Tracked Grants'      },
];

function TeamSharingSection({ T }) {
  const GARAMOND = FONT_H;
  const { role: myRole } = useWorkspace();
  const isOwner = myRole === 'owner';

  const [wsCode,        setWsCode]        = useState(() => localStorage.getItem("gos_workspace_code") || "");
  const [wsMembers,     setWsMembers]     = useState([]);
  const [visitorCodes,  setVisitorCodes]  = useState([]);
  const [visitorScope,  setVisitorScope]  = useState({ tables: ['active-grants', 'pipeline-apps'], sections: [] });
  const [joinInput,     setJoinInput]     = useState("");
  const [wsLoading,     setWsLoading]     = useState(false);
  const [visLoading,    setVisLoading]    = useState(false);
  const [visitorDays,   setVisitorDays]   = useState(7);
  const [wsMsg,         setWsMsg]         = useState("");
  const [currentWsOwner, setCurrentWsOwner] = useState(() => localStorage.getItem('gos_ws_owner') || "");

  const msg = (text) => { setWsMsg(text); setTimeout(() => setWsMsg(""), 4000); };
  const isErr = (t) => /error|invalid|expired|not found/i.test(t);

  const loadMembers = async () => {
    try {
      const json = await apiFetch(`${API}/workspace/members`).then(r => r.json());
      setWsMembers(json.members || []);
      setVisitorCodes(json.visitorCodes || []);
      if (json.visitor_scope) setVisitorScope(json.visitor_scope);
      if (json.invite_code) { localStorage.setItem("gos_workspace_code", json.invite_code); setWsCode(json.invite_code); }
    } catch {}
  };

  useEffect(() => { loadMembers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateTeamCode = async () => {
    setWsLoading(true);
    try {
      const json = await apiFetch(`${API}/workspace/create`, { method: "POST" }).then(r => r.json());
      if (json.code) { localStorage.setItem("gos_workspace_code", json.code); setWsCode(json.code); await loadMembers(); msg("Workspace created — share the Team Code with full members."); }
      else msg(json.error || "Failed to create workspace.");
    } catch { msg("Error creating workspace."); }
    setWsLoading(false);
  };

  const generateVisitorCode = async () => {
    setVisLoading(true);
    try {
      const json = await apiFetch(`${API}/workspace/visitor-code`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ days: visitorDays }) }).then(r => r.json());
      if (json.code) { await loadMembers(); msg(`Visitor code generated — expires in ${visitorDays} day${visitorDays > 1 ? "s" : ""}.`); }
      else msg(json.error || "Failed to generate visitor code.");
    } catch { msg("Error generating visitor code."); }
    setVisLoading(false);
  };

  const revokeVisitorCode = async (code) => {
    try {
      await apiFetch(`${API}/workspace/visitor-code/${code}`, { method: "DELETE" });
      await loadMembers();
      msg("Visitor code revoked.");
    } catch { msg("Error revoking code."); }
  };

  const joinWorkspace = async () => {
    if (!joinInput.trim()) return;
    setWsLoading(true);
    try {
      const json = await apiFetch(`${API}/workspace/join`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code: joinInput.trim() }) }).then(r => r.json());
      if (json.success) { if (json.owner) { localStorage.setItem('gos_ws_owner', json.owner); localStorage.setItem('gos_drive_mode', 'team'); setCurrentWsOwner(json.owner); } msg(`Joined as ${json.type === "visitor" ? "visitor" : "team member"}. You're now viewing team data — use the context switcher at the top right to return to your personal data at any time.`); setJoinInput(""); setTimeout(() => window.location.reload(), 1500); }
      else msg(json.error || "Invalid code.");
    } catch { msg("Error joining workspace."); }
    setWsLoading(false);
  };

  const leaveWorkspace = async () => {
    setWsLoading(true);
    try {
      const json = await apiFetch(`${API}/workspace/leave`, { method: "POST" }).then(r => r.json());
      if (json.ok) {
        localStorage.removeItem('gos_ws_owner');
        setCurrentWsOwner("");
        msg("Left workspace. Returning to personal view…");
        setTimeout(() => window.location.reload(), 1500);
      } else {
        msg(json.error || "Failed to leave workspace.");
      }
    } catch { msg("Error leaving workspace."); }
    setWsLoading(false);
  };

  const assignRole = async (memberEmail, role) => {
    try {
      const json = await apiFetch(`${API}/workspace/assign-role`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ memberEmail, role }) }).then(r => r.json());
      if (json.ok) { await loadMembers(); msg(`Role updated: ${memberEmail} → ${role}`); }
      else msg(json.error || "Failed to update role.");
    } catch { msg("Error updating role."); }
  };

  const removeMember = async (memberEmail) => {
    if (!window.confirm(`Remove ${memberEmail} from workspace?`)) return;
    try {
      const json = await apiFetch(`${API}/workspace/remove-member`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ memberEmail }) }).then(r => r.json());
      if (json.ok) { await loadMembers(); msg(`${memberEmail} removed.`); }
      else msg(json.error || "Failed to remove member.");
    } catch { msg("Error removing member."); }
  };

  const saveVisitorScope = async (tables) => {
    try {
      const json = await apiFetch(`${API}/workspace/set-visitor-scope`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tables, sections: [] }) }).then(r => r.json());
      if (json.ok) { setVisitorScope(json.visitor_scope); msg("Visitor scope saved."); }
      else msg(json.error || "Failed to save visitor scope.");
    } catch { msg("Error saving visitor scope."); }
  };

  const fmtExpiry = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const days = Math.ceil((d - now) / 86400000);
    if (days < 0) return "Expired";
    if (days === 0) return "Expires today";
    return `Expires in ${days}d (${d.toLocaleDateString()})`;
  };

  return (
    <div style={{ marginTop: 32, padding: "20px", background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 8 }}>
      <h3 style={{ margin: "0 0 4px", fontFamily: GARAMOND, fontSize: 16, fontWeight: 600, color: T.text }}>🤝 Team Sharing</h3>
      <div style={{ fontSize: 12, color: T.textFaint, fontFamily: GARAMOND, marginBottom: 18, lineHeight: 1.6 }}>
        <strong style={{ color: T.textSub }}>Team codes</strong> are permanent — full access, no expiry.<br />
        <strong style={{ color: T.textSub }}>Visitor codes</strong> expire after the duration you set — ideal for clients or reviewers.
      </div>

      {/* ── Team code (permanent) ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: T.gold, fontFamily: GARAMOND, marginBottom: 8 }}>Team Code — Permanent</div>
        {wsCode ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <code style={{ fontSize: 17, fontWeight: 700, letterSpacing: "0.18em", padding: "7px 14px", background: T.bgApp, border: `1px solid ${T.gold}`, borderRadius: 6, color: T.gold, fontFamily: "monospace" }}>{wsCode}</code>
            <button onClick={() => { navigator.clipboard.writeText(wsCode); msg("Team code copied!"); }} style={{ padding: "6px 13px", background: "transparent", border: `1px solid ${T.border}`, color: T.textMuted, borderRadius: 6, cursor: "pointer", fontSize: 12, fontFamily: GARAMOND }}>Copy</button>
          </div>
        ) : (
          <button onClick={generateTeamCode} disabled={wsLoading} style={{ padding: "8px 18px", background: TOKENS.gold, color: "#fff", border: "none", borderRadius: 6, cursor: wsLoading ? "not-allowed" : "pointer", fontSize: 13, fontFamily: GARAMOND, fontWeight: 600 }}>
            {wsLoading ? "Creating…" : "Create Workspace"}
          </button>
        )}
        {wsMembers.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: T.textFaint, fontFamily: GARAMOND, marginBottom: 6 }}>Members ({wsMembers.length}):</div>
            {wsMembers.map((m, i) => {
              const roleColor = { editor: T.gold, viewer: "#22d3ee", visitor: T.textFaint }[m.role] || T.textFaint;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: `1px solid ${T.bgElevated}`, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, color: T.textSub, fontFamily: GARAMOND, flex: 1, minWidth: 120 }}>{m.email || m.user_id}</span>
                  {isOwner ? (
                    <select value={m.role || "editor"} onChange={e => assignRole(m.email || m.user_id, e.target.value)}
                      style={{ padding: "3px 7px", background: T.bgApp, border: `1px solid ${T.border}`, borderRadius: 4, color: roleColor, fontSize: 11, fontFamily: GARAMOND, outline: "none", cursor: "pointer" }}>
                      <option value="editor">editor</option>
                      <option value="viewer">viewer</option>
                      <option value="visitor">visitor</option>
                    </select>
                  ) : (
                    <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 3, background: `${roleColor}22`, color: roleColor, fontFamily: GARAMOND }}>{m.role || "editor"}</span>
                  )}
                  {isOwner && (
                    <button onClick={() => removeMember(m.email || m.user_id)}
                      style={{ padding: "3px 8px", background: "transparent", border: "1px solid #7f1d1d", color: "#ef4444", borderRadius: 4, cursor: "pointer", fontSize: 11, fontFamily: GARAMOND }}>Remove</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Visitor codes (temporary) ── */}
      {wsCode && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#22d3ee", fontFamily: GARAMOND, marginBottom: 10 }}>Visitor Codes — Temporary</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <select value={visitorDays} onChange={e => setVisitorDays(Number(e.target.value))}
              style={{ padding: "6px 10px", background: T.bgApp, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 12, fontFamily: GARAMOND, outline: "none" }}>
              <option value={1}>Expires in 1 day</option>
              <option value={7}>Expires in 7 days</option>
              <option value={14}>Expires in 14 days</option>
              <option value={30}>Expires in 30 days</option>
            </select>
            <button onClick={generateVisitorCode} disabled={visLoading} style={{ padding: "6px 16px", background: "#0e7490", color: "#cffafe", border: "none", borderRadius: 6, cursor: visLoading ? "not-allowed" : "pointer", fontSize: 12, fontFamily: GARAMOND, fontWeight: 600 }}>
              {visLoading ? "Generating…" : "+ Generate Visitor Code"}
            </button>
          </div>
          {visitorCodes.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {visitorCodes.map((vc, i) => {
                const expired = new Date(vc.expiresAt) < new Date();
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: T.bgApp, border: `1px solid ${expired ? "#7f1d1d" : T.border}`, borderRadius: 6, flexWrap: "wrap" }}>
                    <code style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.12em", color: expired ? "#ef4444" : "#22d3ee", fontFamily: "monospace", flex: 1 }}>{vc.code}</code>
                    <span style={{ fontSize: 11, color: expired ? "#ef4444" : T.textFaint, fontFamily: GARAMOND }}>{fmtExpiry(vc.expiresAt)}</span>
                    {vc.uses > 0 && <span style={{ fontSize: 10, color: T.textFaint, fontFamily: GARAMOND }}>{vc.uses} use{vc.uses > 1 ? "s" : ""}</span>}
                    <button onClick={() => { navigator.clipboard.writeText(vc.code); msg("Visitor code copied!"); }} style={{ padding: "3px 9px", background: "transparent", border: `1px solid ${T.border}`, color: T.textMuted, borderRadius: 4, cursor: "pointer", fontSize: 11, fontFamily: GARAMOND }}>Copy</button>
                    <button onClick={() => revokeVisitorCode(vc.code)} style={{ padding: "3px 9px", background: "transparent", border: "1px solid #7f1d1d", color: "#ef4444", borderRadius: 4, cursor: "pointer", fontSize: 11, fontFamily: GARAMOND }}>Revoke</button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: T.textFaint, fontFamily: GARAMOND, fontStyle: "italic" }}>No active visitor codes. Generate one above.</div>
          )}
        </div>
      )}

      {/* ── Visitor scope (owner-only) ── */}
      {isOwner && wsCode && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textFaint, fontFamily: GARAMOND, marginBottom: 8 }}>Visitor Access — Tables</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {VISITOR_TABLES.map(({ key, label }) => {
              const checked = (visitorScope.tables || []).includes(key);
              return (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: T.textSub, fontFamily: GARAMOND }}>
                  <input type="checkbox" checked={checked}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...(visitorScope.tables || []), key]
                        : (visitorScope.tables || []).filter(t => t !== key);
                      saveVisitorScope(next);
                    }}
                    style={{ width: 14, height: 14, cursor: "pointer" }} />
                  {label}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Join workspace ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
        <div style={{ fontSize: 11, color: T.textFaint, fontFamily: GARAMOND, marginBottom: 8 }}>Join a workspace (enter a team or visitor code):</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input value={joinInput} onChange={e => setJoinInput(e.target.value.toUpperCase())} placeholder="Enter code e.g. AB12CD or V-XY34Z"
            style={{ padding: "7px 12px", background: T.bgApp, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, fontSize: 13, fontFamily: "monospace", outline: "none", flex: 1, minWidth: 180, letterSpacing: "0.1em" }} />
          <button onClick={joinWorkspace} disabled={wsLoading || !joinInput.trim()} style={{ padding: "7px 16px", background: "#059669", color: "#fff", border: "none", borderRadius: 6, cursor: (wsLoading || !joinInput.trim()) ? "not-allowed" : "pointer", fontSize: 13, fontFamily: GARAMOND, fontWeight: 600 }}>Join</button>
        </div>
      </div>

      {/* ── Leave workspace (only shown when member of a workspace) ── */}
      {currentWsOwner && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, marginTop: 14 }}>
          <div style={{ fontSize: 11, color: T.textFaint, fontFamily: GARAMOND, marginBottom: 8 }}>
            Currently viewing: <strong style={{ color: "#60a5fa" }}>{currentWsOwner}</strong>
          </div>
          <button onClick={leaveWorkspace} disabled={wsLoading} style={{ padding: "7px 16px", background: "transparent", border: "1px solid #7f1d1d", color: "#ef4444", borderRadius: 6, cursor: wsLoading ? "not-allowed" : "pointer", fontSize: 13, fontFamily: GARAMOND }}>
            Leave Workspace
          </button>
        </div>
      )}

      {wsMsg && <div style={{ marginTop: 12, fontSize: 12, color: isErr(wsMsg) ? "#ef4444" : "#4ade80", fontFamily: GARAMOND }}>{wsMsg}</div>}
    </div>
  );
}

export { ChangePasswordSection, OrgProfileView, DataPrivacySection, DeleteAccountSection, TeamSharingSection };
